import React, {useEffect, useState, useRef, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import searchIcon from '../../assets/search-glass.png';
import PageBase from "../../components/PageBase";
import Datatable from "../../components/Datatable";
import columns from './column.js';
import * as userActions from "../../redux/User/actions";
import ToggleButton from "../../components/ToggleButton";
import {isNullOrEmpty} from "../../utils/general";
import {Routes, useNavigate} from 'react-router-dom';
import * as ROUTES from "../../utils/routes";
import Filters from "../../components/Filters";
import './Accounts.scss';
import LoadingPanel from "../../components/LoadingPanel";
import * as staticData from '../../redux/staticData/actions';
import { getDisplayFields, getDefaultFields } from '../../utils/fields/accounts.js';
import ListManager from '../../components/ListManager';
import constants  from '../../utils/constants.js';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import useColumnFilters from '../../hooks/useColumnFilters';
import {createColumnConfigs} from '../../utils/fieldUtils.js';
import Pagination from "../../components/Pagination";

const Accounts = props => {
	const limit = 15;
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [selectedVendor, setSelectedVendor] = useState('aws');
	const [dropdownData, setDropdownData] = useState(null);
	const [listColumns, setListColumns] = useState([]);
	const entityType = `${selectedVendor}_${constants.DISPLAY_ENTITIES.ACCOUNTS}`;
	const [filterResetKey, setFilterResetKey] = useState(0);
	const context = 'account' 

	const { search, setSearch, selectedFilters, setSelectedFilters, clearFilters } = usePersistentFilters(
		`accounts-${selectedVendor}`,
		{ value: '', key: 'account_name', operator: { key: 'contains' } },
		[]
	);
		const [debouncedQuery, setDebouncedQuery] = useState(search);

		const fieldsToUse = useMemo(() => {
  		return createColumnConfigs(getDisplayFields(selectedVendor, listColumns.length === 0,context,listColumns),)
		}, [listColumns, selectedVendor]);


	let members = useSelector(state => state.user)[selectedVendor === 'aws' ? 'awsAccounts' : 'msAccounts'];
	let total = useSelector(state => state.user)[selectedVendor === 'aws' ? 'awsAccountsTotal' : 'msAccountsTotal'];
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const inputRef = useRef();
	const defaultFields = useMemo(
		() => getDisplayFields(selectedVendor,true).map(f => f.field),
		[selectedVendor]
	);
	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		resetWith,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: members,
		columns:fieldsToUse,
		storageKey: `${selectedVendor}_accounts`,
		initialState: {}
	});

	const totalPages = Math.ceil(total / limit);


	useEffect(() => {
		const load = async () => {
			setLoading(true);
			const offset = (page - 1) * limit;
			const filters = debouncedQuery.value ? [debouncedQuery, ...selectedFilters] : [...selectedFilters];
			await dispatch(userActions.getVendorAccounts(selectedVendor, limit, offset, filters));
			setLoading(false);
			inputRef?.current?.focus();
		};
		load();
	}, [selectedVendor, debouncedQuery.value, page, limit, selectedFilters]);

	useEffect(() => {
		dispatch(staticData.loadStaticData((data) => {
			setDropdownData(data);
		}));
	}, []);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedQuery(search);
		}, 500);
		return () => {
			clearTimeout(handler);
		};
	}, [search.value]);

	// Update filtered data when members change
	useEffect(() => {
		updateData(members);
	}, [members]);

	if(members == null) {
		return (
			<PageBase title="Accounts" hideRefresh>
				<div className="accounts-header">
					<div className="accounts-search-box">
						<ToggleButton options={['aws', 'ms']} value={selectedVendor} onToggle={() => updateVendor(selectedVendor === 'aws' ? 'ms' : 'aws')} />
					</div>
				</div>
			</PageBase>
		)
	}

	const updateVendor = (vendor) => {
		setSelectedVendor(vendor);
		setPage(1);
	};

	const updateFilter = (value) => {
		setPage(1);
		const obj = {
			"key": "account_name",
			"value": value.toLowerCase(),
			"operator": {
				"key": "search",
			}
		}
		setSearch(obj);
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
	};

	const onRowClick = async (row, index) => {
	const report ={
		 field: `${selectedVendor}_account_name`,
		 value: row.account_name,
		 relatedTable: "contacts",
		};

			navigate(`/account/${selectedVendor}/${row.account_id}`, { 
					state: { 
							row, 
							title: "Account",
							titleName: row.account_name,
							headerLinkTexts:"All Accounts", 
							headerLinkTarget: ROUTES.ACCOUNTS, 
							report
					} 
			});
	};

	const onAddFilter = (filter) => {
		setPage(1);
		const index = selectedFilters.findIndex(f => f.name === filter.name);

		if(index < 0) {
			setSelectedFilters([...selectedFilters, filter]);
		}
		else {
			const updated = [...selectedFilters];
			updated[index] = filter;
			setSelectedFilters(updated);
		}
	};

	const onRemoveFilter = (filterToRemove) => {
		setPage(1);
		setSelectedFilters(selectedFilters.filter(f => f.key !== filterToRemove.key));
	};

	const availableFilters = [
			{ label: 'Name', key: 'account_name', type: 'text' },
			{ label: 'HQ', key: 'hq_id', type: 'list',values: dropdownData?.country },
			{ label: 'Address', key: 'address', type: 'text' },
			{ label: 'industry', key: 'industry_id', type: 'list', values: dropdownData?.industry },
			{ label: 'region', key: 'region_id', type: 'list', values: dropdownData?.region},
			{ label: 'website', key: 'website', type: 'text' },
	];
	  
	// Get default columns based on vendor
	
	return (
		<PageBase title="Accounts"
		          hideRefresh
		>
			{ loading && <LoadingPanel /> }
			<div className="accounts-header">
				<div className="accounts-search-box">
					<ToggleButton options={['aws', 'ms']} value={selectedVendor} onToggle={() => updateVendor(selectedVendor === 'aws' ? 'ms' : 'aws')} />
					<img alt="" src={searchIcon} style={{padding: '0 10px', marginLeft: '20px'}} />
					<input ref={inputRef} className="accounts-search-input" type="text" value={search.value} onChange={e => updateFilter(e.target.value)} placeholder="Search Accounts..." />
					<Filters
							availableFilters={availableFilters}
							selectedFilters={selectedFilters}
							onAddFilter={onAddFilter}
							onRemoveFilter={onRemoveFilter}
							clearFilters={clearFilters}
					/>
				</div>

				<div style={{ flexGrow: 1, paddingRight: 20, justifyContent: 'flex-end', display: 'flex', alignItems: 'center' }}>
					<ListManager
						entityType={entityType}
						displayFields={getDisplayFields(selectedVendor,false,context)}
						defaultFields={defaultFields}
						onColumnsChange={setListColumns}
					/>
				</div>
			</div>

			{selectedFilters.length > 0 && (
				<div>
					<p style={{ color: '#062229', fontWeight: 700, fontSize: 14 }}>Found {total || 0} Accounts</p>
				</div>
			)}

			<Datatable
				data={filteredData}
				filterOrder={filterOrder}
				allData={members}
				columns={fieldsToUse}
				filters={filters}
				onFilterChange={handleFilterChange}
				sortBy={sortBy}
				sortOrder={sortOrder}
				uniqueValuesByColumn={uniqueValuesByColumn}
				onRowClick={onRowClick}
				gridHeight="calc(100vh - 200px) !important"
			/>

			<Pagination
				entityLabel='Accounts'
				showPageSizeSelector={false}
				currentPage={page}
				totalPages={totalPages}
				totalItems={total}
				pageSize={limit}
				onPageChange={handlePageChange}
			/>
		</PageBase>
	)
};

export default Accounts;
